'use client';

// Explicit @web alias: the trade zone has its own `@/lib/api/history/hooks`
// module that would shadow this import when the popover renders there.
import { useActiveOrders } from '@web/lib/api/history/hooks';
import { useMemo } from 'react';
import { useOpenPositionsCount, useProfileInfo } from '@/lib/api/history/profile-hooks';
import type { Coin } from '@/lib/api/transaction/models';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { useCoinsBalance } from '@/lib/providers/CoinsBalanceProvider';
import { useCoins } from '@/lib/providers/CoinsProvider';

export interface BalancePopoverData {
  availableTrading: number;
  totalTrading: number;
  /** Coin of the trading-balance tab: the header's currency, or USDC when absent. */
  tradingCoin: Coin | undefined;
  tradingRate: number;
  totalAssetsUSD: number;
  availableAssetsUSD: number;
  positionValue: number;
  /** Σ unrealized PnL of the open prediction positions. */
  unrealizedPnl: number;
  openPositions: number;
  openOrders: number;
  coins: Coin[];
  isLoading: boolean;
}

const COUNT_PAGE_SIZE = 100;

export function useBalancePopoverData(currency = 'USDC'): BalancePopoverData {
  const { user } = useAuthContext();
  const userId = user?.id ?? '';

  const { coins } = useCoins();
  const { getAvailableBalance, balances } = useCoinsBalance();

  const usdcCoin = useMemo(() => coins.find((c) => c.shortName.toUpperCase() === 'USDC'), [coins]);
  const tradingCoin = useMemo(
    () => coins.find((c) => c.shortName.toUpperCase() === currency.toUpperCase()) ?? usdcCoin,
    [coins, currency, usdcCoin],
  );

  const tradingRate = tradingCoin?.usdRate ?? 1;
  const availableTrading = (tradingCoin ? getAvailableBalance(tradingCoin.id) : 0) ?? 0;
  const totalTrading = tradingCoin ? (balances[tradingCoin.id]?.balance ?? 0) : 0;

  const profileInfo = useProfileInfo(userId);
  const {
    count: openPositions,
    firstPage: openPositionsFirstPage,
    isLoading: isPositionsLoading,
  } = useOpenPositionsCount(userId);
  const activeOrdersQuery = useActiveOrders({ size: COUNT_PAGE_SIZE });

  const positionValue = profileInfo.data?.cards.positionValue ?? 0;
  // shortcut: sums the first COUNT_PAGE_SIZE positions only; page through if a player can hold more.
  const unrealizedPnl = useMemo(
    () => (openPositionsFirstPage?.items ?? []).reduce((sum, p) => sum + p.pnlValue, 0),
    [openPositionsFirstPage],
  );
  const openOrders = activeOrdersQuery.data?.items.length ?? 0;

  const activeCoins = useMemo(
    () => coins.filter((c) => c.active === 'ACTIVE' && c.usdRate != null),
    [coins],
  );

  // Total Assets covers every coin balance converted to USD, not just USDC.
  const { totalCoinsUSD, availableCoinsUSD } = useMemo(() => {
    let total = 0;
    let available = 0;
    for (const coin of coins) {
      const b = balances[coin.id];
      if (!b || coin.usdRate == null) continue;
      total += b.balance * coin.usdRate;
      available += (b.balance - b.frozen) * coin.usdRate;
    }
    return { totalCoinsUSD: total, availableCoinsUSD: available };
  }, [coins, balances]);

  const totalAssetsUSD = totalCoinsUSD + positionValue;
  const availableAssetsUSD = availableCoinsUSD + positionValue;

  return {
    availableTrading,
    totalTrading,
    tradingCoin,
    tradingRate,
    totalAssetsUSD,
    availableAssetsUSD,
    positionValue,
    unrealizedPnl,
    openPositions,
    openOrders,
    coins: activeCoins,
    isLoading: profileInfo.isLoading || isPositionsLoading || activeOrdersQuery.isLoading,
  };
}
